import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { motion } from 'motion/react';
import { http } from '@/network/http';
import { ADMIN_COLORS, staggerContainer, staggerItem } from '@/features/admin/utils/adminAnimations';
import { getActionColor } from '@/features/admin/utils/adminHelpers';
import type { AuditStatsResponse } from '@/features/admin/utils/adminHelpers';
import type { WidgetProps } from '../widgetRegistry';

export function AdminActivityBreakdownWidget({ height }: WidgetProps) {
  const { t } = useTranslation();
  const compact = height < 200;

  const { data: auditStats, isLoading } = useQuery<AuditStatsResponse>({
    queryKey: ['audit-stats-dashboard'],
    queryFn: async () => {
      const { data } = await http.get('/audit/stats');
      return data;
    },
  });

  const activityByAction = auditStats?.data?.byAction ?? [];
  const totalActions = auditStats?.data?.total ?? 1;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-5 w-5 animate-spin" style={{ color: ADMIN_COLORS.textTertiary }} />
      </div>
    );
  }

  if (activityByAction.length === 0) {
    return (
      <p className="text-center py-8 text-sm" style={{ color: ADMIN_COLORS.textTertiary }}>
        {t('admin.noActivityData')}
      </p>
    );
  }

  return (
    <div className={compact ? 'overflow-y-auto max-h-full' : ''}>
      <motion.div className="space-y-3" variants={staggerContainer} initial="hidden" animate="visible">
        {activityByAction.map((action) => {
          const percentage = ((action.count / totalActions) * 100).toFixed(1);
          const color = getActionColor(action.action);
          return (
            <motion.div key={action.action} variants={staggerItem}>
              <div className="flex items-center justify-between mb-1">
                {!compact && (
                  <span className="text-xs font-medium" style={{ color: ADMIN_COLORS.navy }}>
                    {t(`audit.actions.${action.action}`, action.action)}
                  </span>
                )}
                <span className="text-xs font-semibold" style={{ color }}>
                  {action.count}
                </span>
              </div>
              <div className="h-2 rounded-full" style={{ backgroundColor: color + '10' }}>
                <div
                  className="h-2 rounded-full transition-all duration-500"
                  style={{ backgroundColor: color, width: `${percentage}%` }}
                />
              </div>
            </motion.div>
          );
        })}
      </motion.div>
    </div>
  );
}
